import { useCallback } from 'react';
import { formatDateForFilename, sanitizeFilename } from '@orca360/shared';
import { useDocumentAPI } from './useDocumentAPI';
import type { InstructorAssessmentExportData } from '../types/instructor-assessment-export';

export interface UsePDFExportInstructorAssessmentOptions {
  onExportStart?: () => void;
  onExportSuccess?: (blob: Blob, filename: string) => void;
  onExportError?: (error: Error) => void;
}

export interface UsePDFExportInstructorAssessmentReturn {
  exportInstructorAssessmentToPDF: (data: InstructorAssessmentExportData, sessionId?: string) => Promise<Blob>;
  isExporting: boolean;
  error: Error | null;
}

export function usePDFExportInstructorAssessment(
  options: UsePDFExportInstructorAssessmentOptions = {},
): UsePDFExportInstructorAssessmentReturn {
  const { generateDocument, isExporting, error } = useDocumentAPI(options);

  const exportInstructorAssessmentToPDF = useCallback(
    async (data: InstructorAssessmentExportData, sessionId?: string): Promise<Blob> => {
      const instructorName = sanitizeFilename(data.partA.instructor.name || 'Unknown');
      const sessionDate = formatDateForFilename(data.partA.sessionDate || new Date().toISOString().slice(0, 10));
      const defaultFilename = `WingMentor_Instructor_Assessment_${sessionDate}_${instructorName}.pdf`;
      // sessionId lets the endpoint resolve the owning tenant's effective
      // framework version for authority exports (HG-2B G2-05d).
      return generateDocument('instructor-assessment', 'pdf', data as unknown as Record<string, unknown>, defaultFilename, sessionId);
    },
    [generateDocument],
  );

  return { exportInstructorAssessmentToPDF, isExporting, error };
}
